import type { CaseIntakeParsed } from '../api/CaseIntakeApi';
import { toDateInputValue } from './applyParsedTicketData';
import { caseLicenseType, clientLicenseClass } from './licenseClass';

/**
 * The review form of "Create case": what the document filled in, what staff changed, and what must
 * still be done before Create can be pressed. Pure, so the rules can be tested without a browser.
 */
export interface CaseIntakeForm {
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  driversLicenseNumber: string;
  licenseClass: '' | 'CDL' | 'Regular';
  phone: string;
  email: string;
  clientAddress: string;
  ticketNumber: string;
  citation: string;
  address: string;
  city: string;
  county: string;
  courtDate: string;
  respondBy: string;
  licenseType: '' | 'Class C' | 'CDL' | 'None';
  zone: string;
  agency: string;
  agencyCaseNumber: string;
  plate: string;
}

export type CaseIntakeField = keyof CaseIntakeForm;

/** Where a field's value came from, shown beside it so staff know what to check. */
export type FieldOrigin = 'document' | 'empty' | 'edited';

export function emptyCaseIntakeForm(): CaseIntakeForm {
  return {
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    driversLicenseNumber: '',
    licenseClass: '',
    phone: '',
    email: '',
    clientAddress: '',
    ticketNumber: '',
    citation: '',
    address: '',
    city: '',
    county: '',
    courtDate: '',
    respondBy: '',
    licenseType: '',
    zone: '',
    agency: '',
    agencyCaseNumber: '',
    plate: '',
  };
}

export { caseLicenseType, clientLicenseClass };

function joinAddress(parsed: CaseIntakeParsed): string {
  const cityLine = [parsed.clientCity, [parsed.clientState, parsed.clientZip].filter(Boolean).join(' ')]
    .filter((part) => part && part.trim())
    .join(', ');
  return [parsed.clientAddress, cityLine].filter((part) => part && part.trim()).join(', ');
}

/**
 * The form as the document filled it, and which fields it filled.
 *
 * A field the document did not supply stays empty and is marked so: nothing is defaulted, copied from
 * another field or guessed. In particular the respond-by date is never filled from the court date —
 * they are different dates, and a copied one is a deadline nobody printed.
 */
export function formFromParsed(parsed: CaseIntakeParsed | null): {
  form: CaseIntakeForm;
  fromDocument: CaseIntakeField[];
} {
  const form = emptyCaseIntakeForm();
  if (!parsed) return { form, fromDocument: [] };

  const text = (value: string | null) => (value ?? '').trim();
  const date = (value: string | null) => (text(value) ? toDateInputValue(text(value)) : '');

  form.firstName = text(parsed.firstName);
  form.middleName = text(parsed.middleName);
  form.lastName = text(parsed.lastName);
  form.dateOfBirth = date(parsed.dateOfBirth);
  form.driversLicenseNumber = text(parsed.driversLicenseNumber);
  form.licenseClass = clientLicenseClass(parsed.driversLicenseClass);
  form.phone = text(parsed.phone);
  form.email = text(parsed.email);
  form.clientAddress = joinAddress(parsed);
  form.ticketNumber = text(parsed.ticketNumber);
  form.citation = text(parsed.citation);
  form.address = text(parsed.address);
  form.city = text(parsed.city);
  form.county = text(parsed.county);
  form.courtDate = date(parsed.courtDate);
  form.respondBy = date(parsed.respondBy);
  form.licenseType = caseLicenseType(parsed.licenseType);
  if (form.licenseType === 'CDL' && form.licenseClass !== 'CDL') {
    form.licenseClass = 'CDL';
  } else if (form.licenseClass === 'CDL' && (!form.licenseType || form.licenseType === 'Class C')) {
    form.licenseType = 'CDL';
  }
  form.agency = text(parsed.issuingAgency);
  form.agencyCaseNumber = text(parsed.agencyCaseNumber);
  form.plate = text(parsed.licensePlate);

  const fromDocument = (Object.keys(form) as CaseIntakeField[]).filter((field) => form[field] !== '');
  return { form, fromDocument };
}

export function fieldOrigin(
  field: CaseIntakeField,
  form: CaseIntakeForm,
  original: CaseIntakeForm,
  fromDocument: CaseIntakeField[],
): FieldOrigin {
  if (form[field] !== original[field]) return 'edited';
  return fromDocument.includes(field) && form[field] !== '' ? 'document' : 'empty';
}

/** The staff create-client form's rule, which the server applies here too. */
export function nameProblem(value: string, label: string, required: boolean): string | null {
  const trimmed = value.trim();
  if (!trimmed) return required ? `${label} is required` : null;
  if (trimmed.length > 50) return `${label} must be 50 characters or fewer`;
  if (!/^[a-zA-Z\s]+$/.test(trimmed)) return `${label} can only contain letters and spaces`;
  return null;
}

export interface CreateReadiness {
  form: CaseIntakeForm;
  /** A candidate's guid, 'new', or null when nothing is chosen yet. */
  clientChoice: string | 'new' | null;
  courtGuid: string | null;
  plan: string;
  hasDuplicates: boolean;
  duplicateAcknowledged: boolean;
  genderPronounValid: boolean;
}

/** What still stands between staff and the Create button, in words. Empty means ready. */
export function missingForCreate(state: CreateReadiness): string[] {
  const missing: string[] = [];
  if (state.clientChoice === null) {
    missing.push('Choose the client, or "New client"');
  } else if (state.clientChoice === 'new') {
    const first = nameProblem(state.form.firstName, 'First name', true);
    const middle = nameProblem(state.form.middleName, 'Middle name', false);
    const last = nameProblem(state.form.lastName, 'Last name', true);
    for (const problem of [first, middle, last]) if (problem) missing.push(problem);
  }
  if (!state.courtGuid) missing.push('Choose the court');
  if (!state.form.respondBy.trim()) missing.push('Enter the respond-by date');
  if (!state.plan.trim()) missing.push('Choose a plan');
  if (state.hasDuplicates && !state.duplicateAcknowledged) {
    missing.push('Confirm you want another case for this citation');
  }
  if (!state.genderPronounValid) missing.push('Choose pronouns');
  return missing;
}
